import {
  Box,
  Link,
  Skeleton,
  Slider,
  Typography,
  useTheme,
} from "@mui/material";
import CButton from "components/CButton";
import { FC, Fragment, useEffect, useState } from "react";
import Loan from "typedef/Loan";
import {
  ValuesHELOC,
  ValuesSliderProps,
} from "screens/TypeOfCreditLine/useDefineLoanType";
import TermsOfTheLoan from "screens/TypeOfCreditLine/TermsOfTheLoanHELOC";
import CMultipleMarksOfferInviteLink from "screens/BorrowerInviteRegisterForm/CMultipleMarkOffer";
import useOffer from "./useOffer";
import { usePrivateLabel } from "context/PrivateLabelContext/UsePrivateLabelContextProvider";
import API from "utils/API";
import { PrivateLabelFees } from "typedef/PrivateLabelFees";

type HELOCFormProps = {
  loan: Loan | undefined;
  marks: {
    value: number;
  }[];
  loading: boolean;
  // eslint-disable-next-line
  saveOffer: (value?: any) => Promise<void>;
  isAccepting: boolean;
  errorMessage: string | undefined;
  newValuesSlider: ValuesHELOC | undefined;
  showErrorMessage: boolean;
  initialValuesSliderHELOC: ValuesSliderProps | undefined;
  recalculateOffer:
    | ((
        event: Event | React.SyntheticEvent<Element, Event>,
        value: number | number[],
      ) => void)
    | undefined;
};

const HELOCForm: FC<HELOCFormProps> = (props) => {
  const { privateLabel } = usePrivateLabel();
  const offer = props;
  const loan = props.loan;
  const theme = useTheme();
  const [termsOfTheLoan, setTermsOfTheLoan] = useState(false);
  const { offertOptionsByFico } = useOffer();

  const [fees, setFees] = useState<PrivateLabelFees>();

  useEffect(() => {
    API.get<PrivateLabelFees>(`/get/origination-fees`)
      .then((response) => {
        if ("error" in response) {
          console.error(response);
        } else {
          setFees(response.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching fees:", error);
      });
  }, []);

  return (
    <Fragment>
      <Box py={2} bgcolor={theme.palette.background.paper} borderRadius={8}>
        <Box px={{ xs: 2, md: 6 }} py={{ xs: 3, md: 5 }}>
          <Box
            mb={2}
            display={"flex"}
            alignItems={{ md: "center" }}
            justifyContent={"space-between"}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <Box flex={1} textAlign={"left"} mb={{ xs: 1, md: 0 }}>
              <Typography
                variant="h5"
                color={theme.palette.text.primary}
                fontFamily={"Pro Display Bold"}
              >
                HELOC
              </Typography>
            </Box>
            <Box
              flex={1}
              textAlign={{ xs: "left", md: "right" }}
              mb={{ xs: 1, md: 0 }}
            >
              <Typography
                sx={{
                  display: "flex",
                  justifyContent: { md: "end" },
                  alignItems: "baseline",
                }}
                variant="h6"
                color={theme.palette.text.primary}
                fontSize={"1rem"}
              >
                {offer.loading ? (
                  <Skeleton width={"20%"} height={"2.4rem"} />
                ) : (
                  <Fragment>
                    <Link
                      style={{ textDecoration: "none", cursor: "pointer" }}
                      onClick={() => setTermsOfTheLoan(true)}
                    >
                      <span
                        style={{
                          marginRight: 8,
                          fontSize: "1.5rem",
                          fontWeight: "bolder",
                          color: theme.palette.primary.main,
                        }}
                      >
                        {(
                          Math.floor(
                            (offer?.newValuesSlider?.interestRate ?? 0) * 10000,
                          ) / 100
                        ).toFixed(2)}
                        %
                      </span>
                    </Link>
                    {(
                      Math.floor((offer?.newValuesSlider?.APR ?? 0) * 10000) /
                      100
                    ).toFixed(2)}
                    % APR
                  </Fragment>
                )}
              </Typography>
            </Box>
          </Box>
          <Box>
            {
              <CMultipleMarksOfferInviteLink
                offers={offertOptionsByFico}
                requestedLoanAmount={
                  (loan?.finalOffer?.amount ??
                    loan?.initialOffer?.amount) as number
                }
                interestRate={
                  (loan?.finalOffer?.interestRate ??
                    loan?.initialOffer.interestRate) as number
                }
                originationPoints={fees}
              />
            }

            <Typography
              variant="h6"
              color={theme.palette.text.secondary}
              textAlign={"justify"}
              fontFamily={"Pro Display Light"}
            >
              HELOC is an open line of credit. Borrow the money when you need
              it. Your payments will be interest only on your outstanding
              balance during a 5 year draw period followed by a{" "}
              {loan?.property?.address?.components?.state_abbreviation === "TN"
                ? 10
                : 25}{" "}
              year amortized repayment period. Closing and funding can be as
              fast as 3 days.
            </Typography>
          </Box>
          {offer.initialValuesSliderHELOC?.minDraw !==
            offer.initialValuesSliderHELOC?.maxDraw && (
            <Box my={2}>
              <Typography
                variant="h6"
                color={theme.palette.text.primary}
                textAlign={"justify"}
              >
                Select your initial draw and proceed
              </Typography>
            </Box>
          )}
          {!offer.initialValuesSliderHELOC?.maxDraw &&
          !offer.initialValuesSliderHELOC?.minDraw ? (
            <Skeleton width={"100%"} height={"2.8rem"} />
          ) : (
            offer.initialValuesSliderHELOC?.minDraw !==
              offer.initialValuesSliderHELOC?.maxDraw && (
              <Fragment>
                <Box
                  py={1}
                  sx={{
                    borderTop: { xs: "0.5px solid lightGrey", md: "none" },
                    borderBottom: {
                      xs: "0.5px solid lightGrey",
                      md: "none",
                    },
                  }}
                >
                  <Slider
                    sx={{
                      "& .MuiSlider-thumb": {
                        height: 25,
                        borderRadius: "4px",
                        clipPath:
                          "polygon(0 0, 100% 0, 100% 50%, 50% 100%, 0 50%)",
                      },
                      "& .MuiSlider-rail": {
                        height: 15,
                        color: theme.palette.text.secondary,
                      },
                      "& .MuiSlider-mark": {
                        width: 6,
                        height: 6,
                        borderRadius: 5,
                        color: theme.palette.background.paper,
                      },
                      "& .MuiSlider-track": {
                        height: 15,
                        opacity: "0%",
                        color: theme.palette.text.secondary,
                      },
                      "& .MuiSlider-markLabel": {
                        color: theme.palette.text.secondary,
                        fontFamily: "Pro Display Regular",
                      },
                    }}
                    aria-label="Price"
                    marks={offer.marks}
                    disabled={offer.loading}
                    onChangeCommitted={offer.recalculateOffer}
                    getAriaValueText={(value) => `${value}`}
                    min={offer.initialValuesSliderHELOC?.minDraw ?? 0}
                    max={offer.initialValuesSliderHELOC?.maxDraw ?? 0}
                    defaultValue={offer.initialValuesSliderHELOC?.maxDraw}
                    step={1000}
                  />
                  <Box display={"flex"} justifyContent={"space-between"}>
                    <Typography
                      color={theme.palette.text.secondary}
                      variant="subtitle2"
                    >{`< $${Number(
                      offer.initialValuesSliderHELOC?.minDraw?.toFixed(2),
                    )?.toLocaleString("en-US")}`}</Typography>
                    <Typography
                      color={theme.palette.text.secondary}
                      variant="subtitle2"
                    >{`$${Number(
                      offer.initialValuesSliderHELOC?.maxDraw?.toFixed(2),
                    )?.toLocaleString("en-US")} >`}</Typography>
                  </Box>
                </Box>
              </Fragment>
            )
          )}
          <Box
            display={"flex"}
            flexDirection={{ xs: "column", md: "row" }}
            justifyContent={{ xs: "center", md: "left" }}
            alignItems={{ xs: "center", md: "left" }}
          >
            <Typography
              sx={{ display: "flex", alignItems: "center", gap: 0.4 }}
              mt={{ xs: 2, md: 4 }}
              fontFamily={"Pro Display Semibold"}
            >
              Initial Draw:
              {offer.loading ? (
                <span>
                  <Skeleton width={"4rem"} height={"1.4rem"} />
                </span>
              ) : (
                <span
                  style={{
                    color: theme.palette.primary.main,
                  }}
                >
                  $
                  {Number(
                    offer?.newValuesSlider?.initialDrawAmount,
                  ).toLocaleString("en-US")}
                </span>
              )}
            </Typography>
            <Typography
              mt={{ xs: 2, md: 4 }}
              fontFamily={"Pro Display Light"}
              display={{ xs: "none", md: "block" }}
            >
              <span
                style={{
                  marginLeft: 8,
                  marginRight: 8,
                  color: theme.palette.text.disabled,
                  fontFamily: "Pro Display Semibold",
                }}
              >
                •
              </span>
            </Typography>
            <Typography
              mt={{ xs: 1, md: 4 }}
              fontFamily="Pro Display Semibold"
              sx={{ display: "flex", alignItems: "center", gap: 0.4 }}
            >
              Estimated Monthly Payment:{" "}
              {offer.loading ? (
                <span>
                  <Skeleton width={"4.2rem"} height={"1.4rem"} />
                </span>
              ) : (
                <span
                  style={{
                    color: theme.palette.primary.main,
                  }}
                >
                  $
                  {Number(
                    offer?.newValuesSlider?.monthlyPayment?.toFixed(2),
                  )?.toLocaleString("en-US")}
                </span>
              )}
            </Typography>
          </Box>
          <Box mt={4}>
            <CButton
              fullWidth
              type="submit"
              variant="contained"
              loading={offer.isAccepting}
              onClick={() => offer.saveOffer("HELOC")}
              disabled={loan?.borrowerFlags?.archiveType ? true : false}
            >
              Accept and Complete HELOC Application
            </CButton>
          </Box>
          <Box mt={4}>
            <Typography
              variant="subtitle2"
              textAlign={"justify"}
              color={theme.palette.text.secondary}
            >
              This letter is solely a pre-qualification for terms of a loan with{" "}
              {privateLabel?.lenderName}. NMLS #{privateLabel?.NMLScompany},
              that you may be eligible for based on your stated information.
              This pre-qualification is not a commitment to lend or an approval
              of an application for credit with {privateLabel?.lenderName}. A
              written commitment to make a mortgage loan has not yet been
              issued. This is not a rate lock, and the APR, interest rate, and
              other related terms and conditions shown on this pre-qualification
              are sample terms only and are subject to change until you are
              approved for a loan and a commitment or loan offer is issued. In
              order for {privateLabel?.lenderName} to review your application
              for credit, you will need to submit a complete loan application
              and provide the information detailed above. Please note that the
              final terms and conditions of any loan offered to you will be
              based on {privateLabel?.lenderName} underwriting of and
              verification of the provided information. You are not required to
              obtain a loan from {privateLabel?.lenderName} just because you
              have received this pre-qualification.
            </Typography>
          </Box>
        </Box>
      </Box>
      <TermsOfTheLoan
        loan={loan}
        open={termsOfTheLoan}
        handleClose={() => setTermsOfTheLoan(false)}
      />
    </Fragment>
  );
};

export default HELOCForm;
